import {WebCallService} from "@/utilities/system/services/WebCallService";
import {PathHelper} from "@/utilities/system/helpers/PathHelper";
import {ErrorLocationType, HttpType} from "@/utilities/system/enums/Enums";
import {Provider} from "@/models/entities/providers/Provider";
import NullOrUndefinedHelper from "@/utilities/system/helpers/NullOrUndefinedHelper";
import {GeneralResponse} from "@/models/system/api/Responses";
import {
    ExistsResponse,
    ProviderIdCodeResponse,
    ProviderIdResponse,
    ProviderInfoResponse,
    ProvidersResponse,
    CompaniesResponse
} from "@/models/api/responses";
import {
    ConnectSystemToProviderRequest,
    ProviderRequest
} from "@/models/api/requests";
import ErrorHandler from "@/utilities/system/helpers/ErrorHandler";
import { ResponseValidator } from "@/utilities/system/helpers/ResponseValidator";

export class ProviderService {
    private webCaller: WebCallService;
    private uriHelper: PathHelper;
    private responseValidator: ResponseValidator;

    constructor(){
        this.webCaller = new WebCallService();
        this.uriHelper = new PathHelper();
        this.responseValidator = new ResponseValidator();
    }

    public async getProviderDetails(errorLocationType:ErrorLocationType): Promise<ProvidersResponse|null> {
        const uri = this.uriHelper.providers.getProvidersPath();

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getProvidersByName(name: string, errorLocationType:ErrorLocationType): Promise<CompaniesResponse|null> {
        const uri = this.uriHelper.providers.getProviderByNamePath(name);

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getNewProviderIdCode(errorLocationType:ErrorLocationType): Promise<ProviderIdCodeResponse|null> {
        const uri = this.uriHelper.providers.getProviderIdCodePath();

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async addOrUpdateProvider(provider: Provider, errorLocationType:ErrorLocationType): Promise<ProviderIdResponse|null> {
        const uri = NullOrUndefinedHelper(provider.id)
            ? this.uriHelper.providers.getProvidersPath()
            : this.uriHelper.providers.getProvidersByIdPath(provider.id);

        const requestBody = new ProviderRequest(provider);

        const reqType = NullOrUndefinedHelper(provider.id)
            ? HttpType.post
            : HttpType.put;

        const resp = await this.webCaller.HttpSend(reqType, uri, requestBody);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getProviderById(id: string, errorLocationType:ErrorLocationType): Promise<ProviderInfoResponse|null> {
        const uri = this.uriHelper.providers.getProvidersByIdPath(id);

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    // correct 'deletion API', it only deletes the cross-table reference in anabsystemProvider and not globally deletes the Provider entity (which may be connected to other systems aswell)
    public async deleteSystemConnectionToProvider(id: string, errorLocationType:ErrorLocationType): Promise<GeneralResponse|null> {
        const uri = this.uriHelper.providers.getProvidersByIdPath(id);

        const resp = await this.webCaller.HttpSend(HttpType.delete, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async postConnectSystemToProvider(systemId:(string | undefined) = undefined,
                                             providerId:string,
                                             providerIdCode:string,
                                             info:string,
                                             errorLocationType:ErrorLocationType): Promise<GeneralResponse|null> {
        const uri = this.uriHelper.providers.getConnectSystemToProviderPath();
        const requestBody = new ConnectSystemToProviderRequest(systemId, providerId, providerIdCode, info);

        const resp = await this.webCaller.HttpSend(HttpType.post, uri, requestBody);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async postGetAllSimilarProviders(provider: Provider, errorLocationType:ErrorLocationType): Promise<CompaniesResponse|null> {
        const uri = this.uriHelper.providers.getSimilarProviderPath();
        const requestBody = new ProviderRequest(provider);

        const resp = await this.webCaller.HttpSend(HttpType.post, uri, requestBody);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getProviderHasUserWithRole(id: string, role: string, errorLocationType:ErrorLocationType): Promise<ExistsResponse|null>{
        const uri = this.uriHelper.providers.getProviderHasUserWithRolePath(id, role);

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }
}